import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { ARTBanner } from '#app/components/cotizacion/ARTBanner'
import { ARTCompanyDataScreen } from '#app/components/cotizacion/screens/art/ARTCompanyDataScreen'
import { ARTConfirmationScreen } from '#app/components/cotizacion/screens/art/ARTConfirmationScreen'
import { ARTEmployeesScreen } from '#app/components/cotizacion/screens/art/ARTEmployeesScreen'
import { ARTInfoScreen } from '#app/components/cotizacion/screens/art/ARTInfoScreen'
import { ARTIntroScreen } from '#app/components/cotizacion/screens/art/ARTIntroScreen'
import { ARTPersonalDataScreen } from '#app/components/cotizacion/screens/art/ARTPersonalDataScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendCotizacionARTEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizacion-art'

export const meta: Route.MetaFunction = () => [
	{ title: 'Cotización ART - 100prevalor' },
	{
		name: 'description',
		content: 'Cotiza tu seguro de Accidentes de Trabajo (ART) en minutos',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as ARTFormData

	try {
		await sendCotizacionARTEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

type ARTFormData = {
	razonSocial: string
	cuit: string
	numEmpleados: string
	masaSalarial: string
	ubicacion: string
	nombreApellido: string
	email: string
	telefono: string
}

export default function CotizacionART() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<Partial<ARTFormData>>({})

	const totalSteps = 6

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const getProgressPercentage = () => {
		if (step <= 2) return 0
		const progressMap: Record<number, number> = {
			3: 10,
			4: 50,
			5: 75,
			6: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 3

	const handleNext = async (stepData?: Partial<ARTFormData>) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		// Special handling when moving to confirmation screen (step 6)
		if (step === 5) {
			// Moving from PersonalDataScreen to confirmation
			// Send email here
			const finalData = { ...formData, ...stepData }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/cotizacion-art', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email ART enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email ART')
				}
			} catch (error) {
				console.error('❌ Error al enviar email ART:', error)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleMoreInfo = () => {
		setCompletedSteps((prev) => new Set([...prev, 2]))
		setSearchParams({ step: '2' })
	}

	const handleRestart = () => {
		setFormData({})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return (
					<ARTIntroScreen
						onNext={() => handleNext()}
						onMoreInfo={handleMoreInfo}
					/>
				)
			case 2:
				return <ARTInfoScreen onNext={() => handleNext()} onBack={handleBack} />
			case 3:
				return (
					<ARTCompanyDataScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={{
							razonSocial: formData.razonSocial,
							cuit: formData.cuit,
						}}
					/>
				)
			case 4:
				return (
					<ARTEmployeesScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={{
							numEmpleados: formData.numEmpleados,
							masaSalarial: formData.masaSalarial,
							ubicacion: formData.ubicacion,
						}}
					/>
				)
			case 5:
				return (
					<ARTPersonalDataScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={{
							nombreApellido: formData.nombreApellido,
							email: formData.email,
							telefono: formData.telefono,
						}}
					/>
				)
			case 6:
				return (
					<ARTConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as ARTFormData}
					/>
				)
			default:
				return (
					<ARTIntroScreen
						onNext={() => handleNext()}
						onMoreInfo={handleMoreInfo}
					/>
				)
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="file-text" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						{showProgressBar && (
							<ARTBanner variant="collapsed" position="modal-top" />
						)}

						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="file-text" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
